import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, useInView } from "framer-motion";
import { getGclid, getSource } from "@/utils/gclid";
import {
  Phone, WhatsappLogo, CheckCircle, Clock, ShieldCheck, MapPin, Star,
  ArrowRight, Bug, Drop, Wind, House, Wrench,
  SealCheck, Lightning, Ruler, Timer, CurrencyInr, FrameCorners, DoorOpen
} from "@phosphor-icons/react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PHONE = "9742119056";
const WHATSAPP_URL = `https://wa.me/91${PHONE}?text=Hi%2C%20I%20need%20mosquito%20mesh%20for%20my%20home%20in%20Bangalore`;

const MESH_SERVICES = [
  { name: "Window Mesh", value: "window-mesh" },
  { name: "Single Door Mesh", value: "single-door" },
  { name: "Double Door Mesh", value: "double-door" },
  { name: "Bathroom Mesh", value: "bathroom-mesh" },
  { name: "Balcony Mesh", value: "balcony-mesh" },
  { name: "Full Home Package", value: "full-home" },
];

const GALLERY = [
  { img: "https://customer-assets.emergentagent.com/job_ac-cooling-now/artifacts/vsksiyi3_WhatsApp%20Image%202026-04-28%20at%2012.19.19%20AM.jpeg", label: "Window Mesh" },
  { img: "https://customer-assets.emergentagent.com/job_ac-cooling-now/artifacts/zng1zzln_WhatsApp%20Image%202026-04-28%20at%2012.19.18%20AM%20%282%29.jpeg", label: "Door Mesh" },
  { img: "https://customer-assets.emergentagent.com/job_ac-cooling-now/artifacts/cuwjcfna_WhatsApp%20Image%202026-04-28%20at%2012.19.19%20AM%20%282%29.jpeg", label: "Bathroom Mesh" },
  { img: "https://customer-assets.emergentagent.com/job_ac-cooling-now/artifacts/nntuzen7_WhatsApp%20Image%202026-04-28%20at%2012.19.18%20AM%20%281%29.jpeg", label: "Ventilator Mesh" },
];

const AREAS = [
  "Koramangala", "Indiranagar", "HSR Layout", "Whitefield", "Electronic City",
  "JP Nagar", "Marathahalli", "Hebbal", "Yelahanka", "BTM Layout",
  "Jayanagar", "Banashankari", "Rajajinagar", "Malleshwaram", "Bellandur",
  "Sarjapur Road", "Bommanahalli", "Basavanagudi", "Vijayanagar", "RT Nagar"
];

const REVIEWS = [
  { name: "Meera T.", area: "HSR Layout", text: "Got mesh for all 6 windows and 2 doors. Team came on time, measured everything, and finished in 3 hours. No drilling at all! My kids can finally sleep without mosquito bites." },
  { name: "Sunil K.", area: "Whitefield", text: "Very professional work. The bathroom mesh fits perfectly even around the exhaust fan. Custom cutting done on spot. Highly recommended for apartments." },
  { name: "Kavya N.", area: "Koramangala", text: "Best mosquito mesh service in Bangalore. Price was exactly what they quoted. No hidden charges. Fresh air without mosquitoes — what more do you need?" },
];

function Fade({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-30px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.45, delay }} className={className}>
      {children}
    </motion.div>
  );
}

// ─── HEADER ───
function MeshHeader() {
  return (
    <header data-testid="mesh-header" className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-200/50">
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-14">
        <span className="font-['Plus_Jakarta_Sans'] font-extrabold text-slate-900 text-base tracking-tight">
          HOME TECH REPAIR
        </span>
        <div className="flex items-center gap-2">
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="mesh-header-whatsapp"
            className="hidden sm:flex bg-[#25D366] text-white hover:bg-[#1DA851] font-bold text-sm px-4 py-2 rounded-lg items-center gap-1.5 transition-all">
            <WhatsappLogo size={16} weight="bold" /> WhatsApp
          </a>
          <a href={`tel:${PHONE}`} data-testid="mesh-header-call-btn"
            className="bg-[#ea580c] text-white hover:bg-[#c2410c] font-bold text-sm px-5 py-2.5 rounded-lg transition-all flex items-center gap-1.5">
            <Phone size={16} weight="bold" /> {PHONE}
          </a>
        </div>
      </div>
    </header>
  );
}

// ─── HERO ───
function MeshHero() {
  return (
    <section data-testid="mesh-hero-section" className="pt-14 relative min-h-[92vh] flex items-center overflow-hidden"
      style={{
        backgroundImage: `url(https://images.unsplash.com/photo-1626900159442-1d1cdca491bc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwyfHxvcGVuJTIwbW9kZXJuJTIwaG9tZSUyMHdpbmRvdyUyMGJyaWdodCUyMHN1bm55JTIwaW50ZXJpb3J8ZW58MHx8fHwxNzc3NzkyODk1fDA&ixlib=rb-4.1.0&q=85)`,
        backgroundSize: "cover", backgroundPosition: "center"
      }}>
      <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/85 to-slate-900/60" />
      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <span className="inline-flex items-center gap-1.5 text-xs font-bold tracking-[0.2em] uppercase text-[#ea580c] bg-[#ea580c]/10 px-4 py-1.5 rounded-full mb-6 font-['Outfit']">
                <Bug size={14} weight="bold" /> Mosquito-Free Homes in Bangalore
              </span>
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
              className="font-['Plus_Jakarta_Sans'] text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.08] text-white mb-5">
              Tired of Mosquitoes?
              <br />
              <span className="text-[#ea580c]">We Mesh It. You Rest.</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}
              className="text-base sm:text-lg text-slate-300 leading-relaxed mb-6 max-w-lg font-['Outfit']">
              Custom-fit mosquito mesh for windows, doors & bathrooms. <strong className="text-white">No drilling. No damage.</strong> Installed same day.
              <br />Windows from just <strong className="text-[#ea580c]">₹65/sq ft</strong>.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-3 mb-8">
              <a href={`tel:${PHONE}`} data-testid="mesh-hero-call-btn"
                className="bg-[#ea580c] text-white hover:bg-[#c2410c] font-bold text-lg px-8 py-4 rounded-xl shadow-lg shadow-[#ea580c]/20 transition-all flex items-center justify-center gap-2">
                <Phone size={22} weight="bold" /> Call {PHONE}
              </a>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="mesh-hero-whatsapp-btn"
                className="bg-[#25D366] text-white hover:bg-[#1DA851] font-bold text-lg px-8 py-4 rounded-xl shadow-lg transition-all flex items-center justify-center gap-2">
                <WhatsappLogo size={22} weight="bold" /> Get Free Quote
              </a>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.4 }}
              className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-400 font-['Outfit']">
              <span className="flex items-center gap-1.5"><CheckCircle size={16} weight="fill" className="text-[#ea580c]" /> Custom fit</span>
              <span className="flex items-center gap-1.5"><CheckCircle size={16} weight="fill" className="text-[#ea580c]" /> No drilling</span>
              <span className="flex items-center gap-1.5"><CheckCircle size={16} weight="fill" className="text-[#ea580c]" /> Same-day install</span>
              <span className="flex items-center gap-1.5"><CheckCircle size={16} weight="fill" className="text-[#ea580c]" /> All Bangalore</span>
            </motion.div>
          </div>
          <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.3 }}>
            <MeshForm />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ─── FORM ───
function MeshForm() {
  const [form, setForm] = useState({ name: "", phone: "", area: "", service: "" });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.area || !form.service) { toast.error("Please fill all fields"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/leads`, { ...form, gclid: getGclid(), source: getSource() });
      if (res.data.success) { navigate(`/thank-you?name=${encodeURIComponent(form.name)}&service=mosquito-mesh`); }
    } catch { toast.error("Something went wrong. Please call us directly."); }
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} data-testid="mesh-booking-form" className="bg-white p-6 sm:p-8 rounded-2xl shadow-2xl">
      <h3 className="font-['Plus_Jakarta_Sans'] text-xl font-bold text-slate-900 mb-1">Get Free Quote</h3>
      <p className="text-xs text-slate-500 font-['Outfit'] mb-5">We measure & quote for free. No obligation.</p>
      <div className="space-y-3">
        <input data-testid="mesh-form-name" type="text" placeholder="Your Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="w-full bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm font-['Outfit'] focus:border-[#ea580c] focus:ring-2 focus:ring-[#ea580c]/10 outline-none transition-all" />
        <input data-testid="mesh-form-phone" type="tel" placeholder="Phone Number" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
          className="w-full bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm font-['Outfit'] focus:border-[#ea580c] focus:ring-2 focus:ring-[#ea580c]/10 outline-none transition-all" />
        <input data-testid="mesh-form-area" type="text" placeholder="Your Area in Bangalore" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })}
          className="w-full bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm font-['Outfit'] focus:border-[#ea580c] focus:ring-2 focus:ring-[#ea580c]/10 outline-none transition-all" />
        <Select value={form.service} onValueChange={(val) => setForm({ ...form, service: val })}>
          <SelectTrigger data-testid="mesh-form-service-select" className="w-full bg-slate-50 border border-slate-200 rounded-lg p-3 h-auto text-sm font-['Outfit'] focus:ring-2 focus:ring-[#ea580c]/10">
            <SelectValue placeholder="What do you need?" />
          </SelectTrigger>
          <SelectContent>
            {MESH_SERVICES.map((s) => (
              <SelectItem key={s.value} value={s.value}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <button type="submit" disabled={loading} data-testid="mesh-booking-submit"
        className="w-full mt-5 bg-[#ea580c] text-white hover:bg-[#c2410c] font-bold text-sm px-6 py-4 rounded-xl shadow-lg shadow-[#ea580c]/20 transition-all flex items-center justify-center gap-2 disabled:opacity-50">
        {loading ? "Sending..." : "Get Free Quote"} {!loading && <ArrowRight size={16} weight="bold" />}
      </button>
    </form>
  );
}

// ─── PRICING ───
function PricingSection() {
  const prices = [
    { type: "Window Mesh", price: "65", unit: "per sq ft", icon: FrameCorners, note: "Most popular for apartments" },
    { type: "Single Door Mesh", price: "260", unit: "per sq ft", icon: DoorOpen, note: "Bedroom & kitchen doors" },
    { type: "Double Door Mesh", price: "290", unit: "per sq ft", icon: DoorOpen, note: "Main door & balcony" },
  ];
  return (
    <section data-testid="mesh-pricing-section" className="bg-white py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <Fade>
          <div className="text-center mb-12">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#ea580c] mb-3 block font-['Outfit']">Transparent Pricing</span>
            <h2 className="font-['Plus_Jakarta_Sans'] text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 mb-3">
              Simple, Honest Rates
            </h2>
            <p className="text-slate-500 font-['Outfit'] max-w-md mx-auto">No hidden charges. Free measurement. You pay only after installation.</p>
          </div>
        </Fade>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 max-w-4xl mx-auto">
          {prices.map((p, i) => (
            <Fade key={i} delay={i * 0.08}>
              <div data-testid={`mesh-price-card-${i}`}
                className="bg-slate-50 border border-slate-200 rounded-2xl p-6 text-center hover:border-[#ea580c]/30 hover:shadow-lg transition-all group">
                <div className="w-12 h-12 bg-[#ea580c]/10 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-[#ea580c]/20 transition-colors">
                  <p.icon size={24} weight="duotone" className="text-[#ea580c]" />
                </div>
                <h3 className="font-['Plus_Jakarta_Sans'] text-base font-bold text-slate-900 mb-3">{p.type}</h3>
                <div className="mb-2">
                  <span className="font-['Plus_Jakarta_Sans'] text-4xl font-extrabold text-[#ea580c]">₹{p.price}</span>
                  <span className="text-sm text-slate-500 font-['Outfit'] ml-1">/{p.unit}</span>
                </div>
                <p className="text-xs text-slate-400 font-['Outfit']">{p.note}</p>
              </div>
            </Fade>
          ))}
        </div>
        <Fade delay={0.3}>
          <div className="text-center mt-8">
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="mesh-pricing-whatsapp"
              className="bg-[#25D366] text-white hover:bg-[#1DA851] font-bold text-base px-8 py-4 rounded-xl shadow-lg transition-all inline-flex items-center gap-2">
              <WhatsappLogo size={20} weight="bold" /> Send Measurements on WhatsApp
            </a>
          </div>
        </Fade>
      </div>
    </section>
  );
}

// ─── BENEFITS ───
function BenefitsSection() {
  const benefits = [
    { icon: Ruler, title: "Custom Fit", desc: "Measured and cut for your exact window/door size. Perfect fit every time." },
    { icon: ShieldCheck, title: "No Drilling", desc: "Velcro or magnetic attachment. No damage to walls or frames. Renter-friendly." },
    { icon: Timer, title: "Same-Day Install", desc: "Morning booking? Evening done. Fast professional installation." },
    { icon: Wind, title: "Fresh Air Flows", desc: "Fine mesh keeps bugs out but lets breeze in. No stuffy rooms." },
    { icon: CurrencyInr, title: "Affordable", desc: "Starting ₹65/sq ft for windows. Best rates in Bangalore." },
    { icon: Wrench, title: "Durable Quality", desc: "UV-resistant, washable mesh. Lasts 3-5 years easy." },
  ];
  return (
    <section data-testid="mesh-benefits-section" className="bg-slate-50 py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <Fade>
          <div className="text-center mb-12">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#ea580c] mb-3 block font-['Outfit']">Why Choose Us</span>
            <h2 className="font-['Plus_Jakarta_Sans'] text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">
              Mesh That Actually Works
            </h2>
          </div>
        </Fade>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 lg:gap-6">
          {benefits.map((b, i) => (
            <Fade key={i} delay={i * 0.06}>
              <div data-testid={`mesh-benefit-${i}`} className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-md transition-all">
                <div className="w-10 h-10 bg-[#ea580c]/10 rounded-lg flex items-center justify-center mb-3">
                  <b.icon size={20} weight="duotone" className="text-[#ea580c]" />
                </div>
                <h4 className="font-['Plus_Jakarta_Sans'] text-sm font-bold text-slate-900 mb-1">{b.title}</h4>
                <p className="text-xs text-slate-500 font-['Outfit'] leading-relaxed">{b.desc}</p>
              </div>
            </Fade>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── GALLERY ───
function GallerySection() {
  return (
    <section data-testid="mesh-gallery-section" className="bg-white py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <Fade>
          <div className="text-center mb-12">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#ea580c] mb-3 block font-['Outfit']">Our Real Work</span>
            <h2 className="font-['Plus_Jakarta_Sans'] text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">
              See It Before You Book
            </h2>
          </div>
        </Fade>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {GALLERY.map((item, i) => (
            <Fade key={i} delay={i * 0.1}>
              <div data-testid={`mesh-gallery-${i}`} className="group relative overflow-hidden rounded-2xl border border-slate-200">
                <img src={item.img} alt={item.label} className="w-full h-56 sm:h-64 object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-900/90 to-transparent p-4">
                  <span className="text-white text-sm font-bold font-['Plus_Jakarta_Sans']">{item.label}</span>
                </div>
              </div>
            </Fade>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── TESTIMONIALS ───
function TestimonialsSection() {
  return (
    <section data-testid="mesh-testimonials-section" className="bg-slate-50 py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <Fade>
          <div className="text-center mb-12">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#ea580c] mb-3 block font-['Outfit']">Happy Homes</span>
            <h2 className="font-['Plus_Jakarta_Sans'] text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">
              What Bangalore Says
            </h2>
          </div>
        </Fade>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {REVIEWS.map((r, i) => (
            <Fade key={i} delay={i * 0.1}>
              <div data-testid={`mesh-review-${i}`} className="bg-white border border-slate-200 rounded-2xl p-6 h-full flex flex-col">
                <div className="flex gap-0.5 mb-3">
                  {[...Array(5)].map((_, j) => <Star key={j} size={15} weight="fill" className="text-[#fbbf24]" />)}
                </div>
                <p className="text-sm text-slate-600 font-['Outfit'] leading-relaxed mb-5 flex-1">"{r.text}"</p>
                <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
                  <div className="w-8 h-8 bg-[#ea580c] text-white rounded-full flex items-center justify-center text-xs font-bold">{r.name[0]}</div>
                  <div>
                    <p className="text-sm font-bold text-slate-900 font-['Plus_Jakarta_Sans']">{r.name}</p>
                    <p className="text-xs text-slate-400">{r.area}</p>
                  </div>
                  <SealCheck size={18} weight="fill" className="text-[#ea580c] ml-auto" />
                </div>
              </div>
            </Fade>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── SERVICE AREAS ───
function AreasSection() {
  return (
    <section data-testid="mesh-areas-section" className="bg-white py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <Fade>
          <div className="text-center mb-10">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#ea580c] mb-3 block font-['Outfit']">Coverage</span>
            <h2 className="font-['Plus_Jakarta_Sans'] text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mb-3">
              We Cover All Bangalore
            </h2>
          </div>
        </Fade>
        <Fade delay={0.1}>
          <div className="flex flex-wrap justify-center gap-2 max-w-3xl mx-auto">
            {AREAS.map((a, i) => (
              <span key={i} className="bg-slate-100 text-slate-700 text-sm font-medium font-['Outfit'] px-4 py-2 rounded-full border border-slate-200 hover:border-[#ea580c]/30 hover:bg-[#ea580c]/5 transition-colors">
                {a}
              </span>
            ))}
          </div>
        </Fade>
      </div>
    </section>
  );
}

// ─── FINAL CTA ───
function FinalCTA() {
  return (
    <section data-testid="mesh-final-cta-section" className="bg-slate-900 py-16 md:py-24 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-[#ea580c]/10 via-transparent to-transparent" />
      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 text-center">
        <Fade>
          <Bug size={40} weight="duotone" className="text-[#ea580c] mx-auto mb-4" />
          <h2 className="font-['Plus_Jakarta_Sans'] text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white mb-3">
            Sleep Peaceful Tonight.
            <br />
            <span className="text-[#ea580c]">Get Mesh Installed Today.</span>
          </h2>
          <p className="text-slate-400 font-['Outfit'] text-base mb-2">Windows from ₹65/sq ft. Free measurement. No drilling.</p>
          <a href={`tel:${PHONE}`} data-testid="mesh-final-cta-phone"
            className="font-['Plus_Jakarta_Sans'] text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tighter inline-block my-6 hover:text-[#ea580c] transition-colors cursor-pointer">
            {PHONE}
          </a>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${PHONE}`} data-testid="mesh-final-call-btn"
              className="bg-[#ea580c] text-white hover:bg-[#c2410c] font-bold text-lg px-10 py-5 rounded-xl shadow-lg shadow-[#ea580c]/20 transition-all inline-flex items-center justify-center gap-2">
              <Phone size={22} weight="bold" /> Call Now
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="mesh-final-whatsapp-btn"
              className="bg-[#25D366] text-white hover:bg-[#1DA851] font-bold text-lg px-10 py-5 rounded-xl shadow-lg transition-all inline-flex items-center justify-center gap-2">
              <WhatsappLogo size={22} weight="bold" /> WhatsApp for Free Quote
            </a>
          </div>
        </Fade>
      </div>
    </section>
  );
}

// ─── FOOTER ───
function MeshFooter() {
  return (
    <footer data-testid="mesh-footer" className="bg-slate-900 border-t border-white/5 py-8">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <span className="font-['Plus_Jakarta_Sans'] font-extrabold text-white text-base block">HOME TECH REPAIR</span>
            <span className="text-slate-500 text-sm font-['Outfit']">Mosquito Mesh Installation — Bangalore</span>
          </div>
          <div className="flex items-center gap-5 text-sm text-slate-500 font-['Outfit']">
            <a href={`tel:${PHONE}`} className="hover:text-white transition-colors flex items-center gap-1.5"><Phone size={14} weight="bold" /> {PHONE}</a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors flex items-center gap-1.5"><WhatsappLogo size={14} weight="bold" /> WhatsApp</a>
          </div>
        </div>
        <div className="mt-6 pt-6 border-t border-white/5 text-center text-xs text-slate-600 font-['Outfit']">
          Home Tech Repair Bangalore. Mosquito net for windows, doors & bathrooms.
        </div>
      </div>
    </footer>
  );
}

// ─── STICKY MOBILE CTA ───
function StickyMobileCTA() {
  return (
    <div data-testid="mesh-sticky-cta" className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-white border-t border-slate-200 p-2 flex gap-2">
      <a href={`tel:${PHONE}`} className="flex-1 bg-[#ea580c] text-white font-bold text-sm py-3 rounded-lg flex items-center justify-center gap-1.5">
        <Phone size={16} weight="bold" /> Call Now
      </a>
      <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="flex-1 bg-[#25D366] text-white font-bold text-sm py-3 rounded-lg flex items-center justify-center gap-1.5">
        <WhatsappLogo size={16} weight="bold" /> WhatsApp
      </a>
    </div>
  );
}

// ─── MAIN PAGE ───
export default function MosquitoMeshPage() {
  return (
    <div className="min-h-screen bg-white">
      <MeshHeader />
      <MeshHero />
      <PricingSection />
      <BenefitsSection />
      <GallerySection />
      <TestimonialsSection />
      <AreasSection />
      <FinalCTA />
      <MeshFooter />
      <StickyMobileCTA />
    </div>
  );
}
